<template>
  <RevLoadingScreen
    v-if="productAttributesStatus === 'pending'"
    class="mt-48"
    :text="i18n(translations.loadingText)"
  />
  <div v-else class="grid gap-y-24">
    <RevForm
      :key="selectedCategory?.value"
      class="flex grow flex-col items-stretch gap-y-16 pb-36"
      :errors="lastStepErrors"
      :initial-values="formValues"
      :validate
      @submit="handleSubmit"
    >
      <template #default="{ values, isValid }">
        <template
          v-for="attributeGroup in formAttributes"
          :key="attributeGroup.label"
        >
          <template v-if="attributeGroup?.fields.length">
            <h2 class="heading-2 mb-16 mt-32">{{ attributeGroup.label }}</h2>
            <template
              v-for="attribute in attributeGroup.fields"
              :key="attribute.code"
            >
              <RevInputText
                v-if="attribute.type === 'text'"
                :id="attribute.code"
                v-model="values[attribute.code] as string"
                :description="
                  attribute.required
                    ? i18n(translations.fieldIsRequired)
                    : undefined
                "
                :error="currentErrors[attribute.code]"
                :label="attribute.label"
                :required="attribute.required"
              />
              <RevInputNumber
                v-else-if="isNumberType(attribute.type)"
                :id="attribute.code"
                v-model="values[attribute.code] as number"
                :description="
                  attribute.required
                    ? i18n(translations.fieldIsRequired)
                    : undefined
                "
                :error="currentErrors[attribute.code]"
                :label="attribute.label"
                :required="attribute.required"
              />
              <RevInputSelect
                v-else-if="attribute.type === 'simple-select'"
                :id="attribute.code"
                v-model="values[attribute.code] as string"
                :description="
                  attribute.required
                    ? i18n(translations.fieldIsRequired)
                    : undefined
                "
                :error="currentErrors[attribute.code]"
                :label="attribute.label"
                :options="attribute.options"
                :required="attribute.required"
              />
              <div
                v-else-if="attribute.type === 'image-url'"
                class="flex gap-8"
              >
                <img
                  v-if="values[attribute.code]"
                  :alt="attribute.label"
                  class="rounded-sm h-auto max-h-48 w-48 object-contain"
                  :src="values[attribute.code] as string"
                />

                <RevInputText
                  :id="attribute.code"
                  v-model="values[attribute.code] as string"
                  class="flex-1"
                  :description="
                    attribute.required
                      ? i18n(translations.fieldIsRequired)
                      : undefined
                  "
                  :error="currentErrors[attribute.code]"
                  :label="attribute.label"
                  :required="attribute.required"
                />
              </div>

              <div v-else-if="attribute.type === 'boolean'">
                <CustomCheckbox
                  v-model="values[attribute.code] as string"
                  :label="attribute.label"
                  :options="attribute.options"
                />
              </div>
            </template>
          </template>
        </template>

        <div
          class="bg-static-default-low fixed bottom-0 left-0 right-0 z-50 flex w-full justify-center py-16"
        >
          <RevButton :disabled="!isValid" type="submit" variant="primary">
            {{ i18n(translations.nextStep) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script lang="ts" setup>
import { useAsyncData } from '#imports'
import { computed, ref, watch } from 'vue'

import {
  HttpApiError,
  type ProblemValidationErrorMember,
} from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import {
  RevForm,
  type ValidatorDeclaration,
  makeValidate,
  required,
} from '@ds/components/Form'
import { RevInputNumber } from '@ds/components/InputNumber'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import type { GetProductResponse, ProductCategory } from '~/api/products/types'

import CustomCheckbox from '../CustomCheckbox/CustomCheckbox.vue'

import translations from './ProductDetailsStep.translations'
import {
  getAttributeDefaultValue,
  isNumberType,
} from './ProductDetailsStep.utils'
// As the product attributes are dynamic, the type cannot be specific
type ProductFormValues = Record<string, string | boolean | number>

const props = defineProps<{
  selectedCategory: ProductCategory | null
  currentProductDetails: Record<string, unknown> | null
  product: GetProductResponse | null
  lastStepErrors: ProblemValidationErrorMember[] | null
}>()
const { openErrorToast } = useTheToast()

const emit = defineEmits(['submit'])
const $httpFetch = useHttpFetch()
const {
  data: productAttributes,
  status: productAttributesStatus,
  error: productAttributesError,
} = await useAsyncData(() =>
  $httpFetch('/api/seller-experience/categories/:categoryId/attributes', {
    pathParams: {
      categoryId: props.selectedCategory?.value as string,
    },
  }),
)

const i18n = useI18n()

const currentErrors = computed(() => {
  return Array.isArray(props?.lastStepErrors)
    ? props.lastStepErrors.reduce(
        (acc: Record<string, string>, error) => {
          const fieldName = error.target.split('.').pop() as string

          return { ...acc, [fieldName]: error.title }
        },
        {} as Record<string, string>,
      )
    : ({} as Record<string, string>)
})

if (productAttributesError.value) {
  const formattedError = HttpApiError.fromAnyError(productAttributesError.value)

  openErrorToast({
    title: formattedError.title || '',
    content: formattedError.detail || '',
  })
}

const formAttributes = computed(() => {
  const commonFields = [
    ...(productAttributes.value?.commonFields?.fields || []),
    {
      code: 'mainImageUrl',
      label: i18n(translations.mainImageUrl),
      options: [],
      required: true,
      type: 'image-url',
    },
    {
      code: 'imageUrl2',
      label: i18n(translations.urlImage2),
      options: [],
      required: false,
      type: 'image-url',
    },
    {
      code: 'imageUrl3',
      label: i18n(translations.urlImage3),
      options: [],
      required: false,
      type: 'image-url',
    },
    {
      code: 'imageUrl4',
      label: i18n(translations.urlImage4),
      options: [],
      required: false,
      type: 'image-url',
    },
    {
      code: 'imageUrl5',
      label: i18n(translations.urlImage5),
      options: [],
      required: false,
      type: 'image-url',
    },
    {
      code: 'imageUrl6',
      label: i18n(translations.urlImage6),
      options: [],
      required: false,
      type: 'image-url',
    },
  ]

  const specificFields = productAttributes.value?.specificFields || {
    label: null,
    fields: [],
  }

  return [
    {
      label: productAttributes.value?.commonFields.label || null,
      fields: commonFields.sort((a, b) => {
        // Required fields first, then by label alphabetically
        if (a.required && !b.required) return -1
        if (!a.required && b.required) return 1

        return a.label.localeCompare(b.label)
      }),
    },
    {
      ...specificFields,
      fields:
        specificFields.fields?.sort((a, b) => {
          // Required fields first, then by label alphabetically
          if (a.required && !b.required) return -1
          if (!a.required && b.required) return 1

          return a.label.localeCompare(b.label)
        }) || [],
    },
  ]
})

const attributeFields = computed(() => {
  return formAttributes.value.flatMap((attributeGroup) => attributeGroup.fields)
})

const validate = computed(() => {
  const rules: ValidatorDeclaration<ProductFormValues> = {}

  for (const field of attributeFields.value) {
    if (field.required && field.type !== 'boolean') {
      rules[field.code] = required(i18n(translations.required))
    }
  }

  return makeValidate<ProductFormValues>(rules)
})

const formValues = ref<ProductFormValues>(
  Object.fromEntries([
    ...attributeFields.value.map((field) => [
      field.code,
      props.currentProductDetails?.[field.code] ||
        getAttributeDefaultValue(field.type),
    ]),
  ]),
)

watch(
  () => props.selectedCategory,
  (newCategory, oldCategory) => {
    // Only reset if category actually changed
    if (newCategory?.value !== oldCategory?.value) {
      formValues.value = Object.fromEntries([
        ...attributeFields.value.map((field) => [
          field.code,
          getAttributeDefaultValue(field.type),
        ]),
      ]) as ProductFormValues
    }
  },
  { deep: true },
)

function handleSubmit(attributeValues: ProductFormValues) {
  emit('submit', {
    categoryId: props.selectedCategory?.value,
    ...attributeValues,
  })
}
</script>
